// /src/renderer/constants/toolsConfig.ts
import { AnalysisType } from './analysisConfig';
import { TOOL_GROUP_NAMES } from './labels';
import { PanelType } from './panelConfig';

// 단위 툴
export interface ToolItemConfig {
  id: string;
  name: string;
  isExclusive: boolean;
  isToggleable?: boolean;
  icon? : React.ReactElement;
}

const manualTool = ["brush","eraser","floodFill"]
export const exclusiveToolByPanel: Record<PanelType, string[]> = {
  EmptyPanel: [
    "scroll",
    "pan",
    "zoom",
    "windowLevel",
    "length",
    "angle",
    "spline",
    "freehand",
    "ellipse",
    "rectangle",
    // "pencil",
    "cobbAngle"
  ],
  DICOMPanel: [
    'rotation',
    "scroll",
    "pan",
    "zoom",
    "windowLevel",
    "length",
    "angle",
    "spline",
    "freehand",
    "ellipse",
    "rectangle",
    // "pencil",
    "cobbAngle",
    ...manualTool
  ],
  VRPanel: ['rotation', 'pan', 'zoom', 'windowLevel'],
  Surface3DPanel: ['rotation', 'pan', 'zoom'],
  CPRPanel: ['scroll', 'pan', 'zoom', 'windowLevel'],
  CrossSectionPanel: ['scroll', 'pan', 'zoom', 'windowLevel'],
  ReportPanel: [],
  PlotPanel: [],
}


// 툴 집합
export interface ToolGroupConfig {
  groupName: ToolGroupType;
  toolItems: ToolItemConfig[];
}

// 패널별 툴 집합
export interface ToolCategoryConfig {
  // categoryName: string;
  toolGroups: ToolGroupConfig[];
}

// 기본값 상수 (defaultToolItemConfig)
const defaultToolItemConfig: Pick<ToolItemConfig, 'isExclusive'> = {
  isExclusive: false, // 기본적으로 false로 설정
};

export const TOOL_ITEM_CONFIG: Record<string, ToolItemConfig> = {
  //여기부터 2D 패널용
  scroll: {
    ...defaultToolItemConfig,
    id: 'scroll',
    name: 'Scroll',
    isExclusive: true,
  },
  pan: {
    ...defaultToolItemConfig,
    id: 'pan',
    name: 'Pan',
    isExclusive: true,
  },
  zoom: {
    ...defaultToolItemConfig,
    id: 'zoom',
    name: 'Zoom',
    isExclusive: true,
  },
  windowLevel: {
    ...defaultToolItemConfig,
    id : 'windowLevel',
    name: 'windowLevel',
    isExclusive: true,
  },
  resetView: {
    ...defaultToolItemConfig,
    id: 'resetView',
    name: 'Reset View',
  },
  brightnessContrast: {
    ...defaultToolItemConfig,
    id: 'brightnessContrast',
    name: 'Brightness/Contrast',
    isExclusive: true,
  },
  FitToScreen: {
    ...defaultToolItemConfig,
    id: 'FitToScreen',
    name: 'Fit To Screen',
  },
  OneOnOneResolution: {
    ...defaultToolItemConfig,
    id: 'OneOnOneResolution',
    name: '1:1 Resolution',
  },
  // pop context menu
  WLPreset: {
    ...defaultToolItemConfig,
    id: 'WLPreset',
    name: 'WL Preset',
  },
  roll: {
    ...defaultToolItemConfig,
    id: 'roll',
    name: 'Roll',
    isExclusive: true,
  },
  rotateCW90: {
    ...defaultToolItemConfig,
    id: 'rotateCW90',
    name: 'Rotate CW90°',
  },
  rotateCCW90: {
    ...defaultToolItemConfig,
    id: 'rotateCCW90',
    name: 'Rotate CCW90°',
  },
  flipHorizontal: {
    ...defaultToolItemConfig,
    id: 'flipHorizontal',
    name: 'Flip Horizontal',
  },
  flipVertical: {
    ...defaultToolItemConfig,
    id: 'flipVertical',
    name: 'Flip Vertical',
  },
  resetRotation: {
    ...defaultToolItemConfig,
    id: 'resetRotation',
    name: 'Reset Rotation',
  },
  // pop context menu
  projection: {
    ...defaultToolItemConfig,
    id: 'projection',
    name: 'Projection',
  },
  length: {
    ...defaultToolItemConfig,
    id: 'length',
    name: 'Length',
    isExclusive: true,
  },
  angle: {
    ...defaultToolItemConfig,
    id: 'angle',
    name: 'Angle',
    isExclusive: true,
  },
  cobbAngle: {
    ...defaultToolItemConfig,
    id: 'cobbAngle',
    name: 'Cobb Angle',
    isExclusive: true,
  },
  brush: {
    ...defaultToolItemConfig,
    id: 'brush',
    name: 'Brush',
    isExclusive: true,
  },
  eraser: {
    ...defaultToolItemConfig,
    id: 'eraser',
    name: 'Eraser',
    isExclusive: true,
  },
  floodFill: {
    ...defaultToolItemConfig,
    id: 'floodFill',
    name: 'FloodFill',
    isExclusive: true,
  },
  connectPoint: {
    ...defaultToolItemConfig,
    id: 'connectPoint',
    name: 'Point Connect',
    isExclusive: true,
  },
  connect2D: {
    ...defaultToolItemConfig,
    id: 'connect2D',
    name: 'Connect 2D',
    isExclusive: true,
  },
  openCurve: {
    ...defaultToolItemConfig,
    id: 'openCurve',
    name: 'Open Curve',
    isExclusive: true,
  },
  closedCurve: {
    ...defaultToolItemConfig,
    id: 'closedCurve',
    name: 'Closed Curve',
    isExclusive: true,
  },
  ellipse: {
    ...defaultToolItemConfig,
    id: 'ellipse',
    name: 'Ellipse',
    isExclusive: true,
  },
  rectangle: {
    ...defaultToolItemConfig,
    id: 'rectangle',
    name: 'Rectangle',
    isExclusive: true,
  },
  pencil: {
    ...defaultToolItemConfig,
    id: 'pencil',
    name: 'Pencil',
    isExclusive: true,
  },
  arrow: {
    ...defaultToolItemConfig,
    id: 'arrow',
    name: 'Arrow',
    isExclusive: true,
  },
  spline: {
    ...defaultToolItemConfig,
    id: 'spline',
    name: 'Spline ROI',
    isExclusive: true,
  },
  freehand: {
    ...defaultToolItemConfig,
    id: 'freehand',
    name: 'Freehand ROI',
    isExclusive: true,
  },
  // AutoCapture 관련
  threeDmultiview: {
    ...defaultToolItemConfig,
    id: 'threeDmultiview',
    name: '3D Multi-view',
  },
  singlecprview: {
    ...defaultToolItemConfig,
    id: 'singlecprview',
    name: 'Single CPR',
  },
  maincprview: {
    ...defaultToolItemConfig,
    id: 'maincprview',
    name: 'Main CPR',
  },
  fullcprview: {
    ...defaultToolItemConfig,
    id: 'fullcprview',
    name: 'Full CPR',
  },
  singlechamberview: {
    ...defaultToolItemConfig,
    id: 'singlechamberview',
    name: 'Single Chamber',
  },
  fullchamberview: {
    ...defaultToolItemConfig,
    id: 'fullchamberview',
    name: 'Full Chamber',
  },
  //껏다 켰다는 switch가 붙게 될것임
  switchAnnotation: {
    ...defaultToolItemConfig,
    id: 'switchAnnotation',
    name: 'Show/Hide Annotation',
    isToggleable:true
  },
  switchMeasurement: {
    ...defaultToolItemConfig,
    id: 'switchMeasurement',
    name: 'Show/Hide Measurement',
    isToggleable:true
  },
  switchPatientData: {
    ...defaultToolItemConfig,
    id: 'switchPatientData',
    name: 'Show/Hide Patient Data',
    isToggleable:true
  },
  switchDICOMTag: {
    ...defaultToolItemConfig,
    id: 'switchDICOMTag',
    name: 'Show/Hide DICOM Tag',
    isToggleable:true
  },

  //하위 툴 없는것
  switchCine: {
    ...defaultToolItemConfig,
    id: 'switchCine',
    name: 'Cine',
    isToggleable:true
  },
  copyToClipboard: {
    ...defaultToolItemConfig,
    id: 'copyToClipboard',
    name: 'Copy To Clipboard',
  },
  print: {
    ...defaultToolItemConfig,
    id: 'print',
    name: 'Print',
  },
  addToReport: {
    ...defaultToolItemConfig,
    id: 'addToReport',
    name: 'Add to Report',
  },
  switchViewReport: {
    ...defaultToolItemConfig,
    id: 'switchViewReport',
    name: 'View Report',
    isToggleable:true
  },

  // MPR 패널용
  switchLockAxisCenter: {
    ...defaultToolItemConfig,
    id: 'switchLockAxisCenter',
    name: 'Lock/Unlock axis center',
    isToggleable:true
  },
  thickness: {
    ...defaultToolItemConfig,
    id: 'thickness',
    name: 'Thickness',
    isExclusive: true,
  },
  threeDRotate: {
    ...defaultToolItemConfig,
    id: 'threeDRotate',
    name: '3D Rotate',
    isExclusive: true,
  },
  // OM1,2로 올라갈런지
  copySelectToClipboard: {
    ...defaultToolItemConfig,
    id: 'copySelectToClipboard',
    name: 'Copy select to clipboard',
  },
  // VR 패널용
  rotation: {
    ...defaultToolItemConfig,
    id: 'rotation',
    name: 'Rotation',
    isExclusive: true,
  },
  anterior: {
    ...defaultToolItemConfig,
    id: 'anterior',
    name: 'Anterior',
  },
  posterior: {
    ...defaultToolItemConfig,
    id: 'posterior',
    name: 'Posterior',
  },
  left: {
    ...defaultToolItemConfig,
    id: 'left',
    name: 'Left',
  },
  right: {
    ...defaultToolItemConfig,
    id: 'right',
    name: 'Right',
  },
  superior: {
    ...defaultToolItemConfig,
    id: 'superior',
    name: 'Superior',
  },
  inferior: {
    ...defaultToolItemConfig,
    id: 'inferior',
    name: 'Inferior',
  },
  //여기부터 5개. 아주 고민이 많은 부분
  scissor: {
    ...defaultToolItemConfig,
    id: 'scissor',
    name: 'Scissor',
    isExclusive: true,
  },
  keepIsolateComponent: {
    ...defaultToolItemConfig,
    id: 'keepIsolateComponent',
    name: 'Keep/Isolate Component',
  },
  undo: {
    ...defaultToolItemConfig,
    id: 'undo',
    name: 'Undo',
  },
  redo: {
    ...defaultToolItemConfig,
    id: 'redo',
    name: 'Redo',
  },
  restoreVolume: {
    ...defaultToolItemConfig,
    id: 'restoreVolume',
    name: 'Restore Volume',
  },

  //하위 툴 없는것
  switchSynchronizingPanel: {
    ...defaultToolItemConfig,
    id: 'switchSynchronizingPanel',
    name: 'Sync CT',
    isToggleable:true
  },

  // 이것은 다중 패널이 보여질때만
  copyAllToClipboard: {
    ...defaultToolItemConfig,
    id: 'copyAllToClipboard',
    name: 'Copy All To Clipboard',
  },
  // 이 기능들,, 바꾸자고 얘기가 나온듯한 이 기억..뭐지
  addSelectToReport: {
    ...defaultToolItemConfig,
    id: 'addSelectToReport',
    name: 'Add select to Report',
  },
  addAllToReport: {
    ...defaultToolItemConfig,
    id: 'addAllToReport',
    name: 'Add all to Report',
  },
  // 굳이?
  saveAsSTL: {
    ...defaultToolItemConfig,
    id: 'saveAsSTL',
    name: 'Save as STL',
  },

  // 여기부터 3D Surface 모델에 대한 기능

  // pop context menu
  // Flat, Smooth, WireFame순으로 들어갈거.
  selectShading: {
    ...defaultToolItemConfig,
    id: 'selectShading',
    name: 'Select Shading',
  },
  // 타우에서 헀던것들
  switchClippingPlane: {
    ...defaultToolItemConfig,
    id: 'switchClippingPlane',
    name: 'Show/Hide Clipping Plane',
    isToggleable:true
  },
  // o-axis기준의 격자정도?
  switchGrid: {
    ...defaultToolItemConfig,
    id: 'switchGrid',
    name: 'Show/Hide Grid',
    isToggleable:true
  },
  switchAxes: {
    ...defaultToolItemConfig,
    id: 'switchAxes',
    name: 'Show/Hide Axes',
    isToggleable:true
  },
  // 위의 기능들이 동작하면 대체 무엇을 리셋되는가?
  resetDisplay: {
    ...defaultToolItemConfig,
    id: 'resetDisplay',
    name: 'Reset Display',
  },

  //하위 툴 없는것, 모달이 보일것이다.
  switchSegmentManagerTools: {
    ...defaultToolItemConfig,
    id: 'switchSegmentManagerTools',
    name: 'Segment Manager',
    isToggleable:true
  },

  // 자꾸 이런 내용이 왜
  saveAsSTLNifTi: {
    ...defaultToolItemConfig,
    id: 'saveAsSTLNifTi',
    name: 'Save as STL/NifTi',
  },
};

// export const TOOL_GROUP_CONFIG2: Record<string, ToolGroupConfig> = {
//   resetView: {
//     groupName: 'reset',
//     toolItems: [
//       TOOL_ITEM_CONFIG.resetView,
//     ]
//   },

//   MPRSynchronizing: {
//     groupName: 'synchronizing',
//     toolItems: [
//       TOOL_ITEM_CONFIG.switchSynchronizingPanel,
//     ],
//   },

//   switchConfigs: {
//     groupName: 'annotation',
//     toolItems: [
//       TOOL_ITEM_CONFIG.switchAnnotation,
//       TOOL_ITEM_CONFIG.switchMeasurement,
//       TOOL_ITEM_CONFIG.switchPatientData,
//       TOOL_ITEM_CONFIG.switchDICOMTag,
//     ],
//   },

//   previewRotation: {
//     groupName: 'rotation',
//     toolItems: [
//       TOOL_ITEM_CONFIG.rotateCW90,
//       TOOL_ITEM_CONFIG.rotateCCW90,
//       TOOL_ITEM_CONFIG.flipHorizontal,
//       TOOL_ITEM_CONFIG.flipVertical,
//       TOOL_ITEM_CONFIG.resetRotation,
//     ],
//   },
// }

export const TOOL_GROUP_CONFIG: Record<string, ToolGroupConfig> = {
  blank : {
    groupName:"blank",
    toolItems:[]
  },
  previewNavigation: {
    groupName: 'navigation',
    toolItems: [
      TOOL_ITEM_CONFIG.scroll,
      TOOL_ITEM_CONFIG.pan,
      TOOL_ITEM_CONFIG.zoom,
      TOOL_ITEM_CONFIG.windowLevel
    ],
  },
  previewDisplay: {
    groupName: 'display',
    toolItems: [
      TOOL_ITEM_CONFIG.brightnessContrast,
      TOOL_ITEM_CONFIG.fitToScreen,
      TOOL_ITEM_CONFIG.oneOnOneResolution,
      TOOL_ITEM_CONFIG.WLPreset,
      TOOL_ITEM_CONFIG.projection
    ],
  },
  previewRotation: {
    groupName: 'rotation',
    toolItems: [
      TOOL_ITEM_CONFIG.roll,
      TOOL_ITEM_CONFIG.rotateCW90,
      TOOL_ITEM_CONFIG.rotateCCW90,
      TOOL_ITEM_CONFIG.flipHorizontal,
      TOOL_ITEM_CONFIG.flipVertical,
      TOOL_ITEM_CONFIG.resetRotation,
    ],
  },
  previewMeasurement: {
    groupName: 'measurement',
    toolItems: [
      TOOL_ITEM_CONFIG.length,
      TOOL_ITEM_CONFIG.angle,
      TOOL_ITEM_CONFIG.cobbAngle,
      TOOL_ITEM_CONFIG.openCurve,
      TOOL_ITEM_CONFIG.closedCurve,
      TOOL_ITEM_CONFIG.ellipse,
      TOOL_ITEM_CONFIG.rectangle,
      TOOL_ITEM_CONFIG.pencil,
      TOOL_ITEM_CONFIG.arrow,
    ],
  },
  switchConfigs: {
    groupName: 'annotation',
    toolItems: [
      TOOL_ITEM_CONFIG.switchAnnotation,
      TOOL_ITEM_CONFIG.switchMeasurement,
      TOOL_ITEM_CONFIG.switchPatientData,
      TOOL_ITEM_CONFIG.switchDICOMTag,
      TOOL_ITEM_CONFIG.switchAxes
    ],
  },
  previewCine: {
    groupName: 'cine',
    toolItems: [
      TOOL_ITEM_CONFIG.switchCine
    ],
  },
  // previewExport: {
  //   groupName: 'export',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.copyToClipboard,
  //     TOOL_ITEM_CONFIG.print,
  //     TOOL_ITEM_CONFIG.addToReport,
  //     TOOL_ITEM_CONFIG.switchViewReport,
  //   ],
  // },
  previewExport: {
    groupName: 'report',
    toolItems: [
      TOOL_ITEM_CONFIG.switchViewReport
    ],
  },
  MPRNavigation: {
    groupName: 'navigation',
    toolItems: [
      TOOL_ITEM_CONFIG.scroll,
      TOOL_ITEM_CONFIG.pan,
      TOOL_ITEM_CONFIG.zoom,
      TOOL_ITEM_CONFIG.windowLevel,
      // TOOL_ITEM_CONFIG.switchLockAxisCenter,
    ],
  },
  MPRDisplay: {
    groupName: 'display',
    toolItems: [
      TOOL_ITEM_CONFIG.brightnessContrast,
      TOOL_ITEM_CONFIG.thickness,
      TOOL_ITEM_CONFIG.fitToScreen,
      TOOL_ITEM_CONFIG.oneOnOneResolution,
      TOOL_ITEM_CONFIG.WLPreset,
      TOOL_ITEM_CONFIG.projection,
    ],
  },
  MPRRotation: {
    groupName: 'rotation',
    toolItems: [
      TOOL_ITEM_CONFIG.roll,
      // TOOL_ITEM_CONFIG.threeDRotate,
      TOOL_ITEM_CONFIG.resetRotation,
    ],
  },
  MPRMeasurement: {
    groupName: 'measurement',
    toolItems: [
      TOOL_ITEM_CONFIG.length,
      TOOL_ITEM_CONFIG.angle,
      TOOL_ITEM_CONFIG.cobbAngle,
      TOOL_ITEM_CONFIG.openCurve,
      TOOL_ITEM_CONFIG.closedCurve,
      TOOL_ITEM_CONFIG.ellipse,
      TOOL_ITEM_CONFIG.rectangle,
      TOOL_ITEM_CONFIG.pencil,
      TOOL_ITEM_CONFIG.arrow,
    ],
  },
  // MPRAnnotation: {
  //   groupName: 'annotation',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.switchAnnotation,
  //     TOOL_ITEM_CONFIG.switchMeasurement,
  //     TOOL_ITEM_CONFIG.switchPatientData,
  //     TOOL_ITEM_CONFIG.switchDICOMTag,
  //   ],
  // },
  MPRSynchronizing: {
    groupName: 'synchronizing',
    toolItems: [
      TOOL_ITEM_CONFIG.switchSynchronizingPanel,
    ],
  },
  modification:{
    groupName:'modification',
    toolItems:[
      TOOL_ITEM_CONFIG.brush,
      TOOL_ITEM_CONFIG.eraser,
      TOOL_ITEM_CONFIG.floodFill,
    ]
  },
  moretools: {
    groupName: 'moretools',
    toolItems: [
      TOOL_ITEM_CONFIG.switchSynchronizingPanel,
      TOOL_ITEM_CONFIG.angle,
      TOOL_ITEM_CONFIG.length,
      TOOL_ITEM_CONFIG.cobbAngle,
      TOOL_ITEM_CONFIG.spline,
      TOOL_ITEM_CONFIG.freehand,
      TOOL_ITEM_CONFIG.ellipse,
      TOOL_ITEM_CONFIG.rectangle,
      TOOL_ITEM_CONFIG.rotateCW90,
      TOOL_ITEM_CONFIG.rotateCCW90,
      TOOL_ITEM_CONFIG.flipHorizontal,
      TOOL_ITEM_CONFIG.flipVertical,
    ]
  },
  // MPRExport: {
  //   groupName: 'export',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.copySelectToClipboard,
  //     TOOL_ITEM_CONFIG.copyAllToClipboard,
  //     TOOL_ITEM_CONFIG.print,
  //     TOOL_ITEM_CONFIG.addSelectToReport,
  //     TOOL_ITEM_CONFIG.addAllToReport,
  //     TOOL_ITEM_CONFIG.switchViewReport,
  //   ],
  // },
  MPRExport: {
    groupName: 'report',
    toolItems: [
      TOOL_ITEM_CONFIG.switchViewReport
    ],
  },
  VRNavigation: {
    groupName: 'navigation',
    toolItems: [
      TOOL_ITEM_CONFIG.rotation,
      TOOL_ITEM_CONFIG.pan,
      TOOL_ITEM_CONFIG.zoom,
      TOOL_ITEM_CONFIG.windowLevel,
    ],
  },
  VRDisplay: {
    groupName: 'display',
    toolItems: [
      TOOL_ITEM_CONFIG.brightnessContrast,
      TOOL_ITEM_CONFIG.WLPreset,
      TOOL_ITEM_CONFIG.projection,
    ],
  },
  VRRotation: {
    groupName: 'rotation',
    toolItems: [
      TOOL_ITEM_CONFIG.rotation,
      TOOL_ITEM_CONFIG.anterior,
      TOOL_ITEM_CONFIG.posterior,
      TOOL_ITEM_CONFIG.left,
      TOOL_ITEM_CONFIG.right,
      TOOL_ITEM_CONFIG.superior,
      TOOL_ITEM_CONFIG.inferior,
    ],
  },
  VRMeasurement: {
    groupName: 'measurement',
    toolItems: [
      TOOL_ITEM_CONFIG.length,
      TOOL_ITEM_CONFIG.angle,
      TOOL_ITEM_CONFIG.cobbAngle,
      TOOL_ITEM_CONFIG.openCurve,
      TOOL_ITEM_CONFIG.closedCurve,
      TOOL_ITEM_CONFIG.ellipse,
      TOOL_ITEM_CONFIG.rectangle,
      TOOL_ITEM_CONFIG.arrow,
    ],
  },
  // VRAnnotation: {
  //   groupName: 'annotation',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.switchAnnotation,
  //     TOOL_ITEM_CONFIG.switchMeasurement,
  //     TOOL_ITEM_CONFIG.switchPatientData,
  //     TOOL_ITEM_CONFIG.switchDICOMTag,
  //   ],
  // },
  VRRemoval: {
    groupName: 'removal',
    toolItems: [
      TOOL_ITEM_CONFIG.scissor,
      TOOL_ITEM_CONFIG.keepIsolateComponent,
      TOOL_ITEM_CONFIG.undo,
      TOOL_ITEM_CONFIG.redo,
      TOOL_ITEM_CONFIG.restoreVolume,
    ],
  },
  // VRExport: {
  //   groupName: 'export',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.copyToClipboard,
  //     TOOL_ITEM_CONFIG.copyAllToClipboard,
  //     TOOL_ITEM_CONFIG.print,
  //     TOOL_ITEM_CONFIG.addSelectToReport,
  //     TOOL_ITEM_CONFIG.addAllToReport,
  //     TOOL_ITEM_CONFIG.switchViewReport,
  //     TOOL_ITEM_CONFIG.saveAsSTL,
  //   ],
  // },
  VRExport: {
    groupName: 'report',
    toolItems: [
      TOOL_ITEM_CONFIG.switchViewReport
    ],
  },
  segmentationNavigation: {
    groupName: 'navigation',
    toolItems: [
      TOOL_ITEM_CONFIG.rotation,
      TOOL_ITEM_CONFIG.pan,
      TOOL_ITEM_CONFIG.zoom,
      TOOL_ITEM_CONFIG.windowLevel,
    ],
  },
  segmentationDisplay: {
    groupName: 'display',
    toolItems: [
      TOOL_ITEM_CONFIG.selectShading,
      TOOL_ITEM_CONFIG.switchClippingPlane,
      TOOL_ITEM_CONFIG.switchGrid,
      TOOL_ITEM_CONFIG.resetDisplay,
    ],
  },
  segmentationRotation: {
    groupName: 'rotation',
    toolItems: [
      TOOL_ITEM_CONFIG.anterior,
      TOOL_ITEM_CONFIG.posterior,
      TOOL_ITEM_CONFIG.left,
      TOOL_ITEM_CONFIG.right,
      TOOL_ITEM_CONFIG.superior,
      TOOL_ITEM_CONFIG.inferior,
    ],
  },
  segmentationMeasurement: {
    groupName: 'measurement',
    toolItems: [
      TOOL_ITEM_CONFIG.length,
      TOOL_ITEM_CONFIG.angle,
      TOOL_ITEM_CONFIG.cobbAngle,
      TOOL_ITEM_CONFIG.openCurve,
      TOOL_ITEM_CONFIG.closedCurve,
      TOOL_ITEM_CONFIG.ellipse,
      TOOL_ITEM_CONFIG.rectangle,
      TOOL_ITEM_CONFIG.arrow,
    ],
  },
  // segmentationAnnotation: {
  //   groupName: 'annotation',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.switchAnnotation,
  //     TOOL_ITEM_CONFIG.switchMeasurement,
  //     TOOL_ITEM_CONFIG.switchPatientData,
  //     TOOL_ITEM_CONFIG.switchDICOMTag,
  //   ],
  // },
  segmentationSegmentManager: {
    groupName: 'segmentManager',
    toolItems: [
      TOOL_ITEM_CONFIG.switchSegmentManagerTools,
    ],
  },
  // segmentationExport: {
  //   groupName: 'export',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.copyToClipboard,
  //     TOOL_ITEM_CONFIG.copyAllToClipboard,
  //     TOOL_ITEM_CONFIG.print,
  //     TOOL_ITEM_CONFIG.addSelectToReport,
  //     TOOL_ITEM_CONFIG.addAllToReport,
  //     TOOL_ITEM_CONFIG.switchViewReport,
  //     TOOL_ITEM_CONFIG.saveAsSTLNifTi,
  //   ],
  // },
  // segmentationExport: {
  //   groupName: 'report',
  //   toolItems: [
  //     TOOL_ITEM_CONFIG.switchViewReport
  //   ],
  // },
  resetView: {
    groupName: 'reset',
    toolItems: [
      TOOL_ITEM_CONFIG.resetView,
    ]
  },

  generalAutoCapture: {
    groupName: 'autocapture',
    toolItems: [
      TOOL_ITEM_CONFIG.threeDmultiview
    ]
  },

  segmentationAutoCapture: {
    groupName: 'autocapture',
    toolItems: [
      TOOL_ITEM_CONFIG.threeDmultiview
    ]
  },

  chamberAutoCapture: {
    groupName: 'autocapture',
    toolItems: [
      TOOL_ITEM_CONFIG.singlechamberview,
      TOOL_ITEM_CONFIG.fullchamberview
    ]
  },

  plaqueAutoCapture: {
    groupName: 'autocapture',
    toolItems: [
      TOOL_ITEM_CONFIG.singlecprview,
      TOOL_ITEM_CONFIG.maincprview,
      TOOL_ITEM_CONFIG.fullcprview
    ]
  },

  report :{
    groupName: 'report',
    toolItems: [
      TOOL_ITEM_CONFIG.switchViewReport
    ],
  },

  measurement:{
    groupName: 'measurement',
    toolItems: [
      TOOL_ITEM_CONFIG.length,
      TOOL_ITEM_CONFIG.angle,
      TOOL_ITEM_CONFIG.cobbAngle,
      TOOL_ITEM_CONFIG.openCurve,
      TOOL_ITEM_CONFIG.closedCurve,
      TOOL_ITEM_CONFIG.ellipse,
      TOOL_ITEM_CONFIG.rectangle,
      TOOL_ITEM_CONFIG.arrow,
    ],
  }

};
// type AnalysisType =
//   | 'preview'
//   | 'MPR'
//   | 'segmentation'
//   | 'aorticValveCalcium'
//   | 'chamber'
//   | 'calciumScore'
//   | 'ejectionFraction'
//   | 'fai'
//   | 'isr'
//   | 'myocardial'
//   | 'wallthickness'
//   | 'plaque'
//   | 'stenosis'
//   | 'empty';


const gen = (tgc1 : ToolGroupConfig,tgc2 : ToolGroupConfig) =>[
  TOOL_GROUP_CONFIG.resetView,
  TOOL_GROUP_CONFIG.segmentationSegmentManager,
  TOOL_GROUP_CONFIG.report,
  TOOL_GROUP_CONFIG.switchConfigs,
]

export const TOOL_CATEGORY_CONFIG: Record<AnalysisType, ToolGroupConfig[]> = {
  empty: [
    TOOL_GROUP_CONFIG.blank,
    TOOL_GROUP_CONFIG.blank,
    TOOL_GROUP_CONFIG.blank,
    TOOL_GROUP_CONFIG.blank,
    TOOL_GROUP_CONFIG.blank,
    TOOL_GROUP_CONFIG.blank,
  ],
  preview: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  MPR: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  segmentation: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.segmentationAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  aorticValveCalcium: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  chamber: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.chamberAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  calciumScore: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  fai: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  isr: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  myocardial: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  wallthickness: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  plaque: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.plaqueAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  stenosis: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  ejectionFraction: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  tavi: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.generalAutoCapture,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
  FFR: [
    TOOL_GROUP_CONFIG.resetView,
    TOOL_GROUP_CONFIG.segmentationSegmentManager,
    TOOL_GROUP_CONFIG.report,
    TOOL_GROUP_CONFIG.moretools,
    TOOL_GROUP_CONFIG.switchConfigs,
  ],
};


// export const TOOL_CATEGORY_CONFIG : Record<string,ToolCategoryConfig>={
//   blank : {
//     categoryName:'',
//     toolGroups:[
//       TOOL_GROUP_CONFIG.blank,
//       TOOL_GROUP_CONFIG.blank,
//       TOOL_GROUP_CONFIG.blank,
//       TOOL_GROUP_CONFIG.blank,
//       TOOL_GROUP_CONFIG.blank,
//       TOOL_GROUP_CONFIG.blank,
//       TOOL_GROUP_CONFIG.blank
//     ]
//   },
//   preview : {
//     categoryName: 'preview',
//     toolGroups:[
//       TOOL_GROUP_CONFIG.previewNavigation,
//       TOOL_GROUP_CONFIG.previewDisplay,
//       TOOL_GROUP_CONFIG.previewRotation,
//       TOOL_GROUP_CONFIG.previewMeasurement,
//       TOOL_GROUP_CONFIG.switchConfigs,
//       TOOL_GROUP_CONFIG.previewCine,
//       TOOL_GROUP_CONFIG.previewExport
//     ]
//   },
//   MPR : {
//     categoryName: PANEL_NAMES.DICOMPanel,
//     toolGroups:[
//       TOOL_GROUP_CONFIG.MPRNavigation,
//       TOOL_GROUP_CONFIG.MPRDisplay,
//       TOOL_GROUP_CONFIG.MPRRotation,
//       TOOL_GROUP_CONFIG.MPRMeasurement,
//       TOOL_GROUP_CONFIG.switchConfigs,
//       TOOL_GROUP_CONFIG.MPRSynchronizing,
//       TOOL_GROUP_CONFIG.MPRExport
//     ]
//   },
//   VR : {
//     categoryName: PANEL_NAMES.VRPanel,
//     toolGroups:[
//       TOOL_GROUP_CONFIG.VRNavigation,
//       TOOL_GROUP_CONFIG.VRDisplay,
//       TOOL_GROUP_CONFIG.VRRotation,
//       TOOL_GROUP_CONFIG.VRMeasurement,
//       TOOL_GROUP_CONFIG.switchConfigs,
//       TOOL_GROUP_CONFIG.VRRemoval,
//       TOOL_GROUP_CONFIG.VRExport
//     ]
//   },
//   segmentation : {
//     categoryName: PANEL_NAMES.Surface3DPanel,
//     toolGroups:[
//       TOOL_GROUP_CONFIG.segmentationNavigation,
//       TOOL_GROUP_CONFIG.segmentationDisplay,
//       TOOL_GROUP_CONFIG.segmentationRotation,
//       TOOL_GROUP_CONFIG.segmentationMeasurement,
//       TOOL_GROUP_CONFIG.switchConfigs,
//       TOOL_GROUP_CONFIG.segmentationSegmentManager,
//       TOOL_GROUP_CONFIG.segmentationExport
//     ]
//   },
//   cpr : {
//     categoryName: PANEL_NAMES.CPRPanel,
//     //임시
//     toolGroups:[
//       TOOL_GROUP_CONFIG.segmentationNavigation,
//       TOOL_GROUP_CONFIG.segmentationDisplay,
//       TOOL_GROUP_CONFIG.segmentationRotation,
//       TOOL_GROUP_CONFIG.segmentationMeasurement,
//       TOOL_GROUP_CONFIG.switchConfigs,
//       TOOL_GROUP_CONFIG.segmentationSegmentManager,
//       TOOL_GROUP_CONFIG.segmentationExport
//     ]
//   },
//   wallthickness : {
//     categoryName: PANEL_NAMES.Surface3DPanel,
//     toolGroups:[
//       TOOL_GROUP_CONFIG.segmentationNavigation,
//       TOOL_GROUP_CONFIG.segmentationDisplay,
//       TOOL_GROUP_CONFIG.segmentationRotation,
//       TOOL_GROUP_CONFIG.segmentationMeasurement,
//       TOOL_GROUP_CONFIG.switchConfigs,
//       TOOL_GROUP_CONFIG.segmentationSegmentManager,
//       TOOL_GROUP_CONFIG.segmentationExport
//     ]
//   }
// }

export type ToolGroupType = keyof typeof TOOL_GROUP_NAMES;
